import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Send, Loader2 } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

interface DirectMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedRecipientId?: string; // Optional: pre-select a recipient
}

export const DirectMessageDialog = ({ open, onOpenChange, preselectedRecipientId }: DirectMessageDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedRecipientId, setSelectedRecipientId] = useState<string>(preselectedRecipientId || "");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Sync selectedRecipientId when preselectedRecipientId changes
  useEffect(() => {
    if (preselectedRecipientId) {
      setSelectedRecipientId(preselectedRecipientId);
    }
  }, [preselectedRecipientId]);

  // Fetch sender profile and network members using channel-based filtering
  const { data: senderProfile } = useQuery({
    queryKey: ['sender-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('display_name, username, organizations')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  // Fetch all profiles for channel-based filtering
  const { data: allProfiles = [], isLoading: isLoadingNetwork } = useQuery({
    queryKey: ['all-profiles-dm', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, username, avatar_url, title, organization, organizations')
        .order('display_name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  // For MVP: Show all profiles except self
  const networkMembers = (() => {
    if (!allProfiles) return [];
    
    return allProfiles.filter(p => p.id !== user?.id); // Exclude self only
  })();

  // Find preselected recipient details
  const preselectedRecipient = preselectedRecipientId 
    ? networkMembers.find(m => m.id === preselectedRecipientId)
    : null;

  const handleSend = async () => {
    console.log('Attempting to send DM:', {
      hasUser: !!user,
      selectedRecipientId,
      messageLength: message.trim().length,
      senderProfile
    });

    if (!user || !selectedRecipientId || !message.trim()) {
      toast({
        title: "Missing information",
        description: "Please select a recipient and enter a message",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);

    try {
      // Find recipient details
      const recipient = networkMembers.find(m => m.id === selectedRecipientId);
      
      if (!recipient) {
        throw new Error("Recipient not found");
      }

      // Create notification for recipient
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: recipient.id,
          type: 'direct_message',
          title: `Message from ${senderProfile?.display_name || 'Unknown'}`,
          message: message.trim(),
          action_url: `/myspai-profile/${senderProfile?.username}`,
          metadata: { sender_id: user.id }
        });

      if (notificationError) throw notificationError;

      // Create sent notification for sender
      const { error: sentNotificationError } = await supabase
        .from('sent_notifications')
        .insert({
          sender_id: user.id,
          recipient_id: recipient.id,
          type: 'direct_message',
          title: `Message to ${recipient.display_name}`,
          message: message.trim(),
          action_url: `/myspai-profile/${recipient.username}`,
          metadata: { recipient_username: recipient.username }
        });

      if (sentNotificationError) throw sentNotificationError;

      toast({
        title: "Message sent",
        description: `Your message was sent to ${recipient.display_name}`,
      });

      // Reset form
      setMessage("");
      if (!preselectedRecipientId) {
        setSelectedRecipientId("");
      }
      onOpenChange(false);

    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Failed to send message",
        description: "There was an error sending your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send Direct Message</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Recipient Selector - Show read-only if preselected, dropdown otherwise */}
          <div className="space-y-2">
            <label className="text-sm font-medium">To:</label>
            {preselectedRecipientId && preselectedRecipient ? (
              <div className="flex items-center gap-2 p-3 border rounded-md bg-muted">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={preselectedRecipient.avatar_url} alt={preselectedRecipient.display_name} />
                  <AvatarFallback>{preselectedRecipient.display_name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col flex-1">
                  <span className="font-medium text-sm">{preselectedRecipient.display_name}</span>
                  {(preselectedRecipient.title || preselectedRecipient.organization) && (
                    <span className="text-xs text-muted-foreground">
                      {[preselectedRecipient.title, preselectedRecipient.organization].filter(Boolean).join(" • ")}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <Select
                value={selectedRecipientId}
                onValueChange={setSelectedRecipientId}
                disabled={isLoadingNetwork || isSending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a network member" />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingNetwork ? (
                    <SelectItem value="loading" disabled>
                      Loading network members...
                    </SelectItem>
                  ) : networkMembers.length === 0 ? (
                    <SelectItem value="empty" disabled>
                      No network members found
                    </SelectItem>
                  ) : (
                    networkMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{member.display_name}</span>
                          {(member.title || member.organization) && (
                            <span className="text-xs text-muted-foreground">
                              {[member.title, member.organization].filter(Boolean).join(" • ")}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Message Textarea */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Message:</label>
            <Textarea
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSending}
              rows={6}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/500 characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!selectedRecipientId || !message.trim() || isSending}
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Message
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
